/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { CartConfiguration, CartsDataActions } from '@msdyn365-commerce/retail-proxy';

/**
 * Input class for the getCartConfiguration data action.
 * The life time of the data action is 'application'.
 */
export class CartConfigurationInput implements IActionInput {
    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'cartConfiguration';

    /**
     * Get Cache type Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'cartConfiguration';

    /**
     * Get Cache type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * CreateInput method for the getCartConfiguration method.
 * @param inputData -- The input data passed to the createInput method.
 * @returns - Input for data action.
 */
export const createCartConfigurationInputInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new CartConfigurationInput();
};

/**
 * The action method for the getCartConfiguration data action.
 * @param input -- The action input.
 * @param ctx -- The action context.
 * @returns -- cart configuration.
 */
export async function getCartConfigurationAction(input: CartConfigurationInput, ctx: IActionContext): Promise<CartConfiguration> {
    const cartConfiguration = await CartsDataActions.getConfigurationAsync({ callerContext: ctx });
    return cartConfiguration;
}

/**
 * The getCartConfiguration data action.
 * Gets the cart configuration state via the read RetailServer API.
 * @returns Address information associated with the retrieved customer.
 */
export const getCartConfigurationDataAction = createObservableDataAction<CartConfiguration>({
    id: '@msdyn365-commerce-modules/retail-actions/get-cart-configuration',
    action: getCartConfigurationAction as IAction<CartConfiguration>,
    input: createCartConfigurationInputInput
});

export default getCartConfigurationDataAction;
